import { EVENTO, NS_CHAT_EQUIPE, type EventoSussurro, type MensagemChat } from '@mymaxx/shared';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { tentarRefresh } from './api';
import { lerAccessToken } from './sessao';
import { tocarAvisoDeMensagem } from './som';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

const ContextoSocket = createContext<Socket | null>(null);

/** Folga antes do vencimento: token que morre no meio do handshake não serve. */
const MARGEM_VALIDADE_MS = 60_000;

/** Quando este access token vence, lido do próprio JWT. `null` = não consegui ler. */
function venceEm(token: string): number | null {
  const corpo = token.split('.')[1];
  if (!corpo) return null;
  try {
    const dados = JSON.parse(atob(corpo.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return typeof dados.exp === 'number' ? dados.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * O token que o servidor vai ver neste handshake — renovado quando está para vencer.
 *
 * O access token dura 15 minutos e o socket se reconecta sozinho (queda de rede, máquina
 * que dormiu, API que subiu num deploy). Mandar o token guardado na primeira montagem
 * fazia a reconexão ser recusada para sempre a partir daí: a aba continuava aberta,
 * bonita, e simplesmente não recebia mais nada até alguém apertar F5 — era isso que
 * obrigava o atendente a recarregar para ver a mídia e o sussurro (28/09).
 */
export async function tokenParaHandshake(): Promise<string | null> {
  const atual = lerAccessToken();
  const vencimento = atual ? venceEm(atual) : null;
  if (atual && (vencimento === null || vencimento - Date.now() > MARGEM_VALIDADE_MS)) return atual;
  await tentarRefresh();
  return lerAccessToken();
}

/**
 * Uma única conexão da equipe para o painel inteiro. Antes cada tela abria a sua; com o
 * painel de sussurro precisando ouvir de qualquer lugar, duas conexões significariam dois
 * heartbeats e eventos chegando pela metade. O provider mora no Layout: enquanto o usuário
 * está logado, o socket está de pé.
 */
export function ProvedorTempoReal({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    if (!lerAccessToken()) return;

    const s: Socket = io(`${BASE_URL}${NS_CHAT_EQUIPE}`, {
      // Função, não objeto: o socket.io chama isto a cada tentativa de conexão, então a
      // reconexão leva um token válido em vez do que existia quando a aba abriu.
      auth: (entregar) => {
        void tokenParaHandshake().then((token) => entregar({ token: token ?? '' }));
      },
      transports: ['websocket', 'polling'],
    });
    setSocket(s);
    return () => {
      s.close();
      setSocket(null);
    };
  }, []);

  return <ContextoSocket.Provider value={socket}>{children}</ContextoSocket.Provider>;
}

export function useSocketEquipe() {
  return useContext(ContextoSocket);
}

interface Ouvintes {
  /** Chegou mensagem numa conversa aberta. */
  aoReceberMensagem: (conversaId: string, mensagem: MensagemChat) => void;
  /** Alguma conversa mudou de estado (entrou na fila, foi atribuída, encerrada). */
  aoMudarConversa: () => void;
  /** O cliente está digitando. */
  aoDigitar?: (conversaId: string) => void;
}

/**
 * Assina os eventos do atendimento. O atendente entra nas salas dos seus setores no
 * handshake; ao abrir uma conversa, pede para entrar também na sala dela.
 */
export function useTempoRealEquipe(conversaAberta: string | null, ouvintes: Ouvintes) {
  const socket = useSocketEquipe();
  const ref = useRef(ouvintes);
  ref.current = ouvintes;
  // Numa ref, e não na dependência do efeito: trocar de conversa não pode desmontar e
  // remontar os ouvintes do socket — no intervalo, mensagem chegando se perde.
  const conversaAbertaRef = useRef(conversaAberta);
  conversaAbertaRef.current = conversaAberta;

  useEffect(() => {
    if (!socket) return;

    const aoMensagem = (m: MensagemChat & { conversaId?: string }) =>
      ref.current.aoReceberMensagem(m.conversaId ?? '', m);
    const aoMudar = () => ref.current.aoMudarConversa();
    const aoDigitar = (e: { conversaId: string }) => ref.current.aoDigitar?.(e.conversaId);
    // Toca em toda mensagem nova do cliente, inclusive na conversa que está aberta na tela
    // (decisão de 03/10). Antes só tocava para o que estava fora da vista, e o atendente
    // reclamou do contrário do esperado: avisava de menos. Quem achar demais desliga no
    // menu — a chave existe justamente para isso, e é por pessoa.
    const aoAvisar = () => {
      tocarAvisoDeMensagem();
      ref.current.aoMudarConversa();
    };

    socket.on(EVENTO.AVISO_MENSAGEM, aoAvisar);
    socket.on(EVENTO.MENSAGEM, aoMensagem);
    socket.on(EVENTO.CONVERSA, aoMudar);
    socket.on(EVENTO.FILA, aoMudar);
    socket.on(EVENTO.PRESENCA, aoMudar);
    socket.on(EVENTO.DIGITANDO, aoDigitar);

    return () => {
      socket.off(EVENTO.AVISO_MENSAGEM, aoAvisar);
      socket.off(EVENTO.MENSAGEM, aoMensagem);
      socket.off(EVENTO.CONVERSA, aoMudar);
      socket.off(EVENTO.FILA, aoMudar);
      socket.off(EVENTO.PRESENCA, aoMudar);
      socket.off(EVENTO.DIGITANDO, aoDigitar);
    };
  }, [socket]);

  // Entra e sai da sala da conversa conforme o atendente troca de aba.
  useEffect(() => {
    if (!socket || !conversaAberta) return;
    socket.emit('abrir', { conversaId: conversaAberta });
    return () => {
      socket.emit('fechar', { conversaId: conversaAberta });
    };
  }, [socket, conversaAberta]);
}

/** Sussurros que chegam enquanto você está em qualquer tela do painel. */
export function useSussurrosRecebidos(aoChegar: (evento: EventoSussurro) => void) {
  const socket = useSocketEquipe();
  const ref = useRef(aoChegar);
  ref.current = aoChegar;

  useEffect(() => {
    if (!socket) return;
    const ouvinte = (e: EventoSussurro) => ref.current(e);
    socket.on(EVENTO.SUSSURRO, ouvinte);
    return () => {
      socket.off(EVENTO.SUSSURRO, ouvinte);
    };
  }, [socket]);
}
